<template>
    <div class="animated fadeIn">
        <LoadingWidget
            v-if="initStatus != 1"
            :status="initStatus"
            @init="init"
        />
        <b-card v-else>
            <h5 id="detail-title" class="mb-0">
                <i :class="typeIconClass + ' mr-2'" />Detail {{ typeLabel }} Emas
            </h5>
            <hr />
            <b-row>
                <b-col id="batch-details" md="6">
                    No. Batch <strong>{{ data.batch_number || "-" }}</strong><br />
                    Tipe <strong>{{ typeLabel }}</strong><br />
                    Status <strong><b-badge :variant="getStatusBadge(data.status)[0]">{{ getStatusBadge(data.status)[1] }}</b-badge></strong><br />
                    Gudang <strong>{{ data.warehouse ? data.warehouse : "-" }}</strong><br />
                    Dibuat oleh <strong>{{ data.created_by ? data.created_by : "-" }}</strong><br />
                    Tanggal Proses <strong>{{ data.processed_at ? $helper.parseDate(data.processed_at) : "-" }}</strong><br />
                    {{ totalRows }} barang<br />
                    Total Berat Bersih: <strong>{{ totalNetWeight }} gram</strong><br />
                </b-col>
            </b-row>
            <b-table
                id="batch-items-table"
                bordered
                hover
                show-empty
                no-local-sorting
                :items="data.items"
                :fields="fields"
                responsive
                class="my-3"
            >
                <template v-slot:empty>
                    Batch tidak memiliki barang.
                </template>
                <template v-slot:table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle mr-2" />
                        <strong>Memuat...</strong>
                    </div>
                </template>
                <template v-slot:cell(no_faktur_pgi)="row">
                    {{ row.item.no_faktur_pgi }}
                    <div class="small text-muted">
                        {{ row.item.item_kind_name || "-" }} |
                        {{ row.item.brand_name || "-" }} <br />
                        {{ row.item.type_name || "-" }}
                    </div>
                </template>
                <template v-slot:cell(dry_weight)="row">
                    {{ row.item.dry_weight ? row.item.dry_weight.toFixed(2) : "0" }} gr
                </template>
                <template v-slot:cell(weight_reduction)="row">
                    {{ row.item.weight_reduction ? row.item.weight_reduction.toFixed(2) : "0" }} gr
                </template>
                <template v-slot:cell(purity)="row">
                    {{ row.item.purity || "-" }}
                </template>
                <template v-slot:cell(sold_price)="row">
                    {{ row.item.sold_price ? "Rp. " + $helper.formatThousand(row.item.sold_price) : "-" }}
                </template>
            </b-table>
            <PageSizer
                :totalRows="totalRows"
                :page="currentPage"
                :options="pageSizeOptions"
                @limit-change="onLimitChange"
                @page-change="onPageChange"
            />

            <div v-if="data.result_inventory">
                <hr />
                <h5><i class="fa fa-fw fa-cube mr-2" />Hasil {{ typeLabel }}</h5>
                <b-row class="mt-3">
                    <b-col md="4">
                        <strong>No. Faktur</strong><br />
                        {{ data.result_inventory.no_faktur_pgi || "-" }}
                    </b-col>
                    <b-col md="4">
                        <strong>Jenis Barang</strong><br />
                        {{ data.result_inventory.kind || "-" }}
                    </b-col>
                    <b-col md="4">
                        <strong>Merk Barang</strong><br />
                        {{ data.result_inventory.brand || "-" }}
                    </b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col md="4">
                        <strong>Tipe</strong><br />
                        {{ data.result_inventory.type || "-" }}
                    </b-col>
                    <b-col md="4">
                        <strong>Kadar</strong><br />
                        {{ data.result_inventory.purity || "-" }}
                    </b-col>
                </b-row>
                <hr />
                <b-row class="mt-3">
                    <b-col md="4">
                        <strong>Berat Kotor</strong><br />
                        {{ data.result_inventory.dry_weight ? data.result_inventory.dry_weight.toFixed(2) : "0" }} gr
                    </b-col>
                    <b-col md="4">
                        <strong>Berat Potongan</strong><br />
                        {{ data.result_inventory.weight_reduction ? data.result_inventory.weight_reduction.toFixed(2) : "0" }} gr
                    </b-col>
                    <b-col md="4">
                        <strong>Berat Bersih</strong><br />
                        {{ resultNetWeight }}
                    </b-col>
                </b-row>
            </div>
        </b-card>
    </div>
</template>

<script>
export default {
    data() {
        return {
            initStatus: 0,
            data: {
                items: [],
                result_inventory: null,
                type: 1
            },

            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            pageSizeOptions: [10, 20, 50],

            fields: [
                { key: "no_faktur_pgi", label: "No. Faktur & Barang", sortable: false },
                { key: "dry_weight", label: "Berat Kotor", sortable: false },
                { key: "weight_reduction", label: "Potongan Berat", sortable: false },
                { key: "purity", label: "Kadar", sortable: false },
                { key: "sold_price", label: "Harga Jual", sortable: false }
            ]
        };
    },

    computed: {
        batchType() {
            return this.data.type || 1;
        },
        typeLabel() {
            return this.batchType === 1 ? "Lebur" : "Pemurnian";
        },
        typeIconClass() {
            return this.batchType === 1 ? "fa fa-fw fa-fire" : "fa fa-fw fa-flask";
        },
        totalNetWeight() {
            if (!this.data.items || !this.data.items.length) return "0.000";
            let total = this.data.items.reduce((sum, it) => {
                let dw = parseFloat(it.dry_weight || 0);
                let wr = parseFloat(it.weight_reduction || 0);
                return sum + (dw - wr);
            }, 0);
            return total.toFixed(3);
        },
        resultNetWeight() {
            if (!this.data.result_inventory) return "-";
            let dw = parseFloat(this.data.result_inventory.dry_weight || 0);
            let wr = parseFloat(this.data.result_inventory.weight_reduction || 0);
            let nw = dw - wr;
            return (isNaN(nw) ? 0 : nw).toFixed(2) + " gr";
        }
    },

    methods: {
        init() {
            this.initStatus = 0;
            this.getBatch(1)
                .then(() => {
                    this.initStatus = 1;
                })
                .catch(() => {
                    this.initStatus = -1;
                });
        },

        getBatch(page) {
            if (isNaN(page)) {
                this.currentPage = 1;
            } else {
                this.currentPage = page;
            }

            let param = this.$helper.getBasePagedQuery(this.currentPage, this.perPage, true);

            return this.$axios
                .get("gold-batches/" + this.$route.params.id + param)
                .then(res => {
                    const resData = res.data.data;
                    this.data = resData;
                    this.totalRows = resData.total || 0;
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                });
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getBatch(this.currentPage);
        },

        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getBatch(this.currentPage);
        },

        getStatusBadge(status) {
            switch (status) {
                case 1:
                    return ["dark", "DRAFT"];
                case 2:
                    return ["success", this.batchType === 1 ? "DILEBUR" : "DIMURNIKAN"];
                default:
                    return ["light", "-"];
            }
        }
    },

    created() {
        this.init();
    }
};
</script>
